'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RoundingCalculator() {
  const t = useTranslations("tools.roundingCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [numberInput, setNumberInput] = useState('');
  const [precision, setPrecision] = useState('0');
  const [roundingMode, setRoundingMode] = useState('nearest');
  const [showSettings, setShowSettings] = useState(false);
  const [results, setResults] = useState<{
    original: number;
    rounded: number;
    method: string;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Rounding functions
  const roundHalfUp = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const floored = Math.floor(scaled);
    const decimal = scaled - floored;

    if (decimal >= 0.5) {
      return (floored + 1) / multiplier;
    }
    return floored / multiplier;
  };

  const roundHalfDown = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const floored = Math.floor(scaled);
    const decimal = scaled - floored;

    if (decimal > 0.5) {
      return (floored + 1) / multiplier;
    }
    return floored / multiplier;
  };

  const roundHalfToEven = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const floored = Math.floor(scaled);
    const decimal = scaled - floored;

    if (decimal === 0.5) {
      return (floored % 2 === 0 ? floored : floored + 1) / multiplier;
    }
    return Math.round(scaled) / multiplier;
  };

  const roundHalfToOdd = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const floored = Math.floor(scaled);
    const decimal = scaled - floored;

    if (decimal === 0.5) {
      return (floored % 2 === 1 ? floored : floored + 1) / multiplier;
    }
    return Math.round(scaled) / multiplier;
  };

  const roundHalfAwayFromZero = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const absScaled = Math.abs(scaled);
    const floored = Math.floor(absScaled);
    const decimal = absScaled - floored;

    let result;
    if (decimal >= 0.5) {
      result = (floored + 1) / multiplier;
    } else {
      result = floored / multiplier;
    }

    return num < 0 ? -result : result;
  };

  const roundHalfTowardZero = (num: number, multiplier: number): number => {
    const scaled = num * multiplier;
    const absScaled = Math.abs(scaled);
    const floored = Math.floor(absScaled);
    const decimal = absScaled - floored;

    let result;
    if (decimal > 0.5) {
      result = (floored + 1) / multiplier;
    } else {
      result = floored / multiplier;
    }

    return num < 0 ? -result : result;
  };

  const roundToFraction = (num: number, denominator: number, mode: string): number => {
    const fraction = 1 / denominator;

    switch(mode) {
      case 'ceil':
        return Math.ceil(num / fraction) * fraction;
      case 'floor':
        return Math.floor(num / fraction) * fraction;
      default:
        return Math.round(num / fraction) * fraction;
    }
  };

  const roundNumber = (num: number, precisionValue: string, mode: string): number => {
    if (precisionValue.startsWith('f')) {
      const denominator = parseInt(precisionValue.substring(1));
      return roundToFraction(num, denominator, mode);
    }

    const precisionInt = parseInt(precisionValue);
    const multiplier = Math.pow(10, precisionInt);

    switch(mode) {
      case 'up':
        return roundHalfUp(num, multiplier);
      case 'down':
        return roundHalfDown(num, multiplier);
      case 'ceil':
        return Math.ceil(num * multiplier) / multiplier;
      case 'floor':
        return Math.floor(num * multiplier) / multiplier;
      case 'even':
        return roundHalfToEven(num, multiplier);
      case 'odd':
        return roundHalfToOdd(num, multiplier);
      case 'away':
        return roundHalfAwayFromZero(num, multiplier);
      case 'toward':
        return roundHalfTowardZero(num, multiplier);
      case 'nearest':
      default:
        return Math.round(num * multiplier) / multiplier;
    }
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) >= 1000000) {
      return num.toLocaleString('en-US', { maximumFractionDigits: 10 });
    }
    return num.toString();
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const num = parseFloat(numberInput);

    if (isNaN(num)) {
      setError(t('errorInvalidNumber'));
      setResults(null);
      return;
    }

    try {
      const rounded = roundNumber(num, precision, roundingMode);
      setResults({
        original: num,
        rounded: rounded,
        method: roundingMode
      });
    } catch (err) {
      setError(t('errorCalculation'));
      setResults(null);
    }
  };

  const handleClear = () => {
    setNumberInput('');
    setPrecision('0');
    setRoundingMode('nearest');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '0.75rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    { maxWidth: '100%', padding: '1.5rem' },
    { maxWidth: '600px', padding: '2rem' },
    { maxWidth: '800px', padding: '2.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { color: colors.textPrimary, margin: '0 0 0.5rem 0', fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const settingsToggleStyle = {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1.5rem'
  };

  const settingsHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none' as const
  };

  const settingsHeaderTitleStyle = getResponsiveStyle(
    { margin: '0', color: '#475569', fontWeight: 600 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const settingsContentStyle = {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e2e8f0',
    display: showSettings ? 'block' : 'none'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    border: '2px solid #fecaca',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = {
    marginTop: '2rem',
    padding: '2rem',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '0.75rem',
    border: '2px solid #86efac',
    display: results ? 'block' : 'none'
  };

  const resultsHeaderStyle = getResponsiveStyle(
    { color: '#166534', margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      border: '1px solid #bbf7d0'
    },
    { flexDirection: 'column' as const, gap: '0.5rem', textAlign: 'center' as const },
    { flexDirection: 'row' as const, gap: '0', textAlign: 'left' as const },
    { flexDirection: 'row' as const, gap: '0', textAlign: 'left' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#166534' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#15803d' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
      color: '#1e40af'
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    paddingLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceStyle = {
    marginBottom: '0.5rem'
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontSize: '0.95rem',
    wordBreak: 'break-all' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <div style={toolDescriptionStyle}>
            {t('toolDescription')}
          </div>

          <form onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="number-input" style={labelStyle}>{t('numberInputLabel')}</label>
              <input
                type="text"
                id="number-input"
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                placeholder={t('numberInputPlaceholder')}
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="precision-select" style={labelStyle}>{t('precisionLabel')}</label>
              <select
                id="precision-select"
                value={precision}
                onChange={(e) => setPrecision(e.target.value)}
                style={selectStyle}
              >
                <optgroup label={t('decimalPlacesGroup')}>
                  <option value="1">{t('tenths')}</option>
                  <option value="2">{t('hundredths')}</option>
                  <option value="3">{t('thousandths')}</option>
                  <option value="4">{t('tenThousandths')}</option>
                  <option value="5">{t('hundredThousandths')}</option>
                  <option value="6">{t('millionths')}</option>
                </optgroup>
                <optgroup label={t('wholeNumbersGroup')}>
                  <option value="0">{t('ones')}</option>
                  <option value="-1">{t('tens')}</option>
                  <option value="-2">{t('hundreds')}</option>
                  <option value="-3">{t('thousands')}</option>
                  <option value="-6">{t('millions')}</option>
                </optgroup>
                <optgroup label={t('fractionsGroup')}>
                  <option value="f2">{t('half')}</option>
                  <option value="f4">{t('quarter')}</option>
                  <option value="f8">{t('eighth')}</option>
                  <option value="f16">{t('sixteenth')}</option>
                  <option value="f32">{t('thirtySecond')}</option>
                  <option value="f64">{t('sixtyFourth')}</option>
                </optgroup>
              </select>
            </div>

            <div style={settingsToggleStyle}>
              <div style={settingsHeaderStyle} onClick={() => setShowSettings(!showSettings)}>
                <h4 style={settingsHeaderTitleStyle}>{t('advancedSettings')}</h4>
                <span>{showSettings ? '▲' : '▼'}</span>
              </div>
              <div style={settingsContentStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('roundingMethodLabel')}</label>
                  <select
                    id="rounding-mode"
                    value={roundingMode}
                    onChange={(e) => setRoundingMode(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="nearest">{t('roundNearest')}</option>
                    <option value="up">{t('roundHalfUp')}</option>
                    <option value="down">{t('roundHalfDown')}</option>
                    <option value="ceil">{t('roundCeil')}</option>
                    <option value="floor">{t('roundFloor')}</option>
                    <option value="even">{t('roundHalfEven')}</option>
                    <option value="odd">{t('roundHalfOdd')}</option>
                    <option value="away">{t('roundHalfAway')}</option>
                    <option value="toward">{t('roundHalfToward')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
              <button type="button" onClick={handleClear} style={clearButtonStyle}>{t('clearButton')}</button>
            </div>
          </form>

          <div style={errorMessageStyle}>
            {error}
          </div>

          <div style={resultsStyle}>
            <h3 style={resultsHeaderStyle}>✨ {t('resultTitle')}</h3>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('originalNumber')}</span>
              <span style={resultValueStyle}>{results && formatNumber(results.original)}</span>
            </div>
            <div style={resultItemStyle}>
              <span style={resultLabelStyle}>{t('roundedValue')}</span>
              <span style={resultValueStyle}>{results && formatNumber(results.rounded)}</span>
            </div>
            <div style={{...resultItemStyle, marginBottom: '0'}}>
              <span style={resultLabelStyle}>{t('methodUsed')}</span>
              <span style={{...resultValueStyle, fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem'}}>
                {results && t(`method_${results.method}`)}
              </span>
            </div>
          </div>

          <div style={infoBoxStyle}>
            💡 <strong>{t('tipLabel')}</strong> {t('tipText')}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle1')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('financeLabel')}</strong> {t('financeText')}</li>
          <li style={listItemStyle}><strong>{t('statisticsLabel')}</strong> {t('statisticsText')}</li>
          <li style={listItemStyle}><strong>{t('educationLabel')}</strong> {t('educationText')}</li>
          <li style={listItemStyle}><strong>{t('scienceLabel')}</strong> {t('scienceText')}</li>
          <li style={listItemStyle}><strong>{t('computingLabel')}</strong> {t('computingText')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('detailsSubtitle2')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('roundNearestLabel')}</strong> {t('roundNearestText')}</li>
          <li style={listItemStyle}><strong>{t('roundHalfUpLabel')}</strong> {t('roundHalfUpText')}</li>
          <li style={listItemStyle}><strong>{t('roundHalfDownLabel')}</strong> {t('roundHalfDownText')}</li>
          <li style={listItemStyle}><strong>{t('roundCeilLabel')}</strong> {t('roundCeilText')}</li>
          <li style={listItemStyle}><strong>{t('roundFloorLabel')}</strong> {t('roundFloorText')}</li>
          <li style={listItemStyle}><strong>{t('roundHalfEvenLabel')}</strong> {t('roundHalfEvenText')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={{...faqItemStyle, marginBottom: '0'}}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={{...glassCardStyle, marginBottom: '0'}}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a href="https://www.graphcalc.com/rounding-methods-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.jotform.com/form-templates/number-rounding-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://apps.apple.com/de/app/rounding-calculator/id1621082259?l=en-GB" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference3')}
          </a>
        </div>
      </section>
    </div>
  );
}